import * as THREE from 'three';

/**
 * SkySystem - Realistischer Himmel mit Wölkchen und gradueller Färbung
 */
export class SkySystem {
  private skyMesh!: THREE.Mesh;
  private cloudMesh!: THREE.Points;
  private skyGroup = new THREE.Group();

  constructor(scene: THREE.Scene) {
    this.createSky();
    this.createClouds();
    scene.add(this.skyGroup);
  }

  private createSky(): void {
    // Himmelssphere mit Farbverlauf
    const skyGeometry = new THREE.SphereGeometry(500, 32, 32);
    
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Blauer Himmel mit Farbverlauf
    const gradient = ctx.createLinearGradient(0, 0, 0, 512);
    gradient.addColorStop(0, '#87CEEB');    // Hellblau oben
    gradient.addColorStop(0.5, '#B0D4E3');  // Mittleres Blau
    gradient.addColorStop(1, '#E0F6FF');    // Sehr helles Blau unten (Horizont)

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1024, 512);

    // Einige Wolken-Flecken als Muster
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    for (let i = 0; i < 20; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 256; // Nur oben
      const w = Math.random() * 100 + 50;
      const h = Math.random() * 50 + 20;
      ctx.fillRect(x, y, w, h);
    }

    const skyTexture = new THREE.CanvasTexture(canvas);
    const skyMaterial = new THREE.MeshBasicMaterial({
      map: skyTexture,
      side: THREE.BackSide
    });

    this.skyMesh = new THREE.Mesh(skyGeometry, skyMaterial);
    this.skyMesh.receiveShadow = false;
    this.skyMesh.castShadow = false;
    this.skyGroup.add(this.skyMesh);
  }

  private createClouds(): void {
    // Wolken-Layer mit beweglichen Partikeln
    const cloudsGeometry = new THREE.BufferGeometry();
    const cloudCount = 150;
    const positions = new Float32Array(cloudCount * 3);
    const sizes = new Float32Array(cloudCount);
    const velocities = new Float32Array(cloudCount * 2);

    for (let i = 0; i < cloudCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 800;      // x
      positions[i * 3 + 1] = Math.random() * 200 + 100;     // y (höher oben)
      positions[i * 3 + 2] = (Math.random() - 0.5) * 800;   // z

      sizes[i] = Math.random() * 60 + 30;
      velocities[i * 2] = (Math.random() - 0.5) * 0.5;      // x velocity
      velocities[i * 2 + 1] = (Math.random() - 0.5) * 0.3;   // z velocity
    }

    cloudsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    cloudsGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    // Wolken-Textur (einfache runde Flecken)
    const cloudTexture = this.createCloudTexture();

    const cloudMaterial = new THREE.PointsMaterial({
      map: cloudTexture,
      size: 1,
      transparent: true,
      opacity: 0.6,
      sizeAttenuation: true
    });

    this.cloudMesh = new THREE.Points(cloudsGeometry, cloudMaterial);
    this.cloudMesh.castShadow = false;
    this.cloudMesh.receiveShadow = false;

    // Speichere Velocities im userData für Animation
    (this.cloudMesh.geometry as any).userData.velocities = velocities;
    (this.cloudMesh.geometry as any).userData.cloudCount = cloudCount;

    this.skyGroup.add(this.cloudMesh);
  }

  private createCloudTexture(): THREE.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Weißer, weiches Kreis-Gradient für realistisches Wolken-Aussehen
    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.8)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);

    return new THREE.CanvasTexture(canvas);
  }

  update(delta: number): void {
    // Animiere Wolken
    if (this.cloudMesh) {
      const positions = (this.cloudMesh.geometry as THREE.BufferGeometry).attributes.position;
      const posArray = positions.array as Float32Array;
      const velocities = (this.cloudMesh.geometry as any).userData.velocities as Float32Array;
      const cloudCount = (this.cloudMesh.geometry as any).userData.cloudCount as number;

      for (let i = 0; i < cloudCount; i++) {
        posArray[i * 3] += velocities[i * 2] * 0.5 * delta;
        posArray[i * 3 + 2] += velocities[i * 2 + 1] * 0.5 * delta;

        // Wrap around
        if (posArray[i * 3] > 400) posArray[i * 3] = -400;
        if (posArray[i * 3] < -400) posArray[i * 3] = 400;
        if (posArray[i * 3 + 2] > 400) posArray[i * 3 + 2] = -400;
        if (posArray[i * 3 + 2] < -400) posArray[i * 3 + 2] = 400;
      }

      positions.needsUpdate = true;
    }

    // Sky folgt Kamera
    this.skyGroup.position.copy((this.skyMesh as any).parent?.position || new THREE.Vector3());
  }

  getGroup(): THREE.Group {
    return this.skyGroup;
  }
}
